import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ShoppingCart, Sparkles, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { fmtCAD, fmtInt, creditPrice, approxResults, resultsSummary, postToHosted, POOL_TONE } from "@/lib/billing";
import BuildYourOwnDialog from "./BuildYourOwnDialog";
import TransactionApprovedDialog from "./TransactionApprovedDialog";

let _k = 0;
const nextKey = () => `line-${Date.now()}-${_k++}`;

function FixedTile({ pack, pools, onAdd, blocked, testid, ratios }) {
  if (!pack) return <div />;
  const tone = POOL_TONE[pack.pool] || POOL_TONE.scrape;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col" data-testid={testid}>
      <div className={"text-[10px] uppercase tracking-wider font-semibold " + tone.text}>{pools?.[pack.pool]?.label || pack.pool}</div>
      <div className="text-3xl font-bold text-slate-900 tabular-nums mt-1 font-display">{fmtInt(pack.credits)}</div>
      <div className="text-[11px] text-slate-500">credits</div>
      <div className="text-[11px] text-slate-500 mt-1 min-h-[28px]">{approxResults(pack.pool, pack.credits, ratios)}</div>
      <div className="mt-2 text-lg font-bold text-slate-900 tabular-nums">{fmtCAD(pack.price_cad)}</div>
      <Button onClick={() => onAdd(pack)} disabled={blocked} variant="outline" className="mt-2 min-h-[40px]" data-testid={`${testid}-add`}>
        <Plus className="h-4 w-4 mr-1.5" /> Add to cart
      </Button>
    </div>
  );
}

function CartLine({ l, price, onRemove }) {
  const title = l.type === "pack" ? `${l.label}${l.qty > 1 ? ` × ${l.qty}` : ""}` : "Custom pack";
  const sub = l.type === "pack"
    ? `${fmtInt(l.credits * l.qty)} ${l.pool} credits`
    : [l.enrichment ? `${fmtInt(l.enrichment)} enrichment` : null, l.scrape ? `${fmtInt(l.scrape)} scrape` : null].filter(Boolean).join(" + ");
  return (
    <li className="flex items-start gap-2 py-2.5" data-testid={`cart-line-${l.key}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">{title}</div>
        <div className="text-[11px] text-slate-500">{sub}</div>
      </div>
      <div className="text-sm font-semibold tabular-nums text-slate-900">{fmtCAD(price)}</div>
      <button onClick={() => onRemove(l.key)} className="text-slate-400 hover:text-rose-600 p-1 transition-colors" title="Remove" data-testid={`cart-remove-${l.key}`}>
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
}

export default function PackShop({ data, onChanged, paidTxn, onPaidConsumed }) {
  const pricing = data?.config?.pricing;
  const ratios = pricing?.results;
  const packs = data?.packs || [];
  const province = data?.status?.billing_province;
  const blocked = ["inactive", "not_live", "exempt"].includes(data?.status?.status);

  const enrichPack = packs.find((p) => p.id === "enrich_5000") || packs.find((p) => p.pool === "enrichment");
  const scrapePack = packs.find((p) => p.id === "scrape_3000") || packs.find((p) => p.pool === "scrape");

  const [cart, setCart] = useState([]);
  const [buildOpen, setBuildOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null); // { txn, mode }
  const [taxQuote, setTaxQuote] = useState(null);

  useEffect(() => {
    if (paidTxn) { setSuccess({ txn: paidTxn, mode: "returned" }); onPaidConsumed?.(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidTxn]);

  const linePrice = useCallback((l) => l.type === "pack"
    ? Number(l.unit_price) * l.qty
    : creditPrice(pricing, "enrichment", l.enrichment) + creditPrice(pricing, "scrape", l.scrape), [pricing]);

  const subtotal = useMemo(() => Math.round(cart.reduce((s, l) => s + linePrice(l), 0) * 100) / 100, [cart, linePrice]);
  const itemCount = cart.reduce((n, l) => n + (l.type === "pack" ? l.qty : 1), 0);
  const cartCredits = useMemo(() => {
    const t = { enrichment: 0, scrape: 0 };
    cart.forEach((l) => {
      if (l.type === "pack") t[l.pool] = (t[l.pool] || 0) + l.credits * l.qty;
      else { t.enrichment += l.enrichment || 0; t.scrape += l.scrape || 0; }
    });
    return t;
  }, [cart]);
  const estimate = resultsSummary(cartCredits, ratios);

  const addPack = (pack) => {
    if (!pack) return;
    setCart((c) => {
      const i = c.findIndex((l) => l.type === "pack" && l.pack_id === pack.id);
      if (i >= 0) { const copy = [...c]; copy[i] = { ...copy[i], qty: copy[i].qty + 1 }; return copy; }
      return [...c, { key: nextKey(), type: "pack", pack_id: pack.id, pool: pack.pool, credits: pack.credits, label: pack.label, unit_price: pack.price_cad, qty: 1 }];
    });
    toast.success(`${pack.label} added to cart`);
  };
  const addCustom = ({ enrichment, scrape }) => {
    setCart((c) => [...c, { key: nextKey(), type: "custom", enrichment, scrape }]);
    toast.success("Custom pack added to cart");
    setCartOpen(true);
  };
  const removeLine = (key) => setCart((c) => c.filter((l) => l.key !== key));

  // Live tax while the cart is open.
  useEffect(() => {
    let alive = true;
    if (!cartOpen || subtotal <= 0 || !province) { setTaxQuote(null); return; }
    (async () => {
      try {
        const { data: q } = await api.post("/billing/tax-quote", { amount_cad: subtotal, province });
        if (alive) setTaxQuote(q);
      } catch { if (alive) setTaxQuote(null); }
    })();
    return () => { alive = false; };
  }, [cartOpen, subtotal, province]);

  const checkout = async () => {
    setBusy(true);
    try {
      const items = [];
      cart.forEach((l) => {
        if (l.type === "pack") for (let i = 0; i < l.qty; i++) items.push({ type: "pack", pack_id: l.pack_id });
        else items.push({ type: "custom", enrichment: l.enrichment, scrape: l.scrape });
      });
      const { data: res } = await api.post("/billing/cart/checkout", { items });
      if (res.mode === "charged") {
        setCart([]); setCartOpen(false);
        setSuccess({ txn: res.transaction, mode: "charged" });
        onChanged?.();
      } else {
        toast.message("Taking you to the secure payment page…");
        postToHosted(res.hosted_url, res.token);
      }
    } catch (e) {
      toast.error("Checkout failed", { description: formatApiError(e) });
    } finally { setBusy(false); }
  };

  const total = taxQuote ? taxQuote.total_cad : subtotal;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="pack-shop">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base md:text-lg font-semibold text-slate-900">Top-up credits</h2>
        <p className="text-[11px] text-slate-500">Prices in CAD before tax · purchased credits never expire</p>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_1.25fr_1fr] gap-3 items-stretch">
        <FixedTile pack={enrichPack} pools={data.pools} onAdd={addPack} blocked={blocked} testid="tile-enrichment" ratios={ratios} />

        <div className="rounded-xl border-2 p-4 flex flex-col items-center text-center justify-center bg-gradient-to-b from-rose-50 to-white"
          style={{ borderColor: "hsl(var(--brand))" }} data-testid="tile-build">
          <div className="h-10 w-10 rounded-full flex items-center justify-center text-white mb-2" style={{ background: "hsl(var(--brand))" }}>
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="text-base font-bold text-slate-900 font-display">Build your own pack</div>
          <p className="text-[11px] text-slate-500 mt-1 max-w-[220px]">Mix enrichment and scrape credits in 500-step dials — pay for exactly what you need.</p>
          <Button onClick={() => setBuildOpen(true)} disabled={blocked} className="mt-3 text-white min-h-[44px] w-full"
            style={{ background: "hsl(var(--brand))" }} data-testid="tile-build-open">
            <Sparkles className="h-4 w-4 mr-2" /> Customize
          </Button>
        </div>

        <FixedTile pack={scrapePack} pools={data.pools} onAdd={addPack} blocked={blocked} testid="tile-scrape" ratios={ratios} />
      </div>

      <div className="mt-4 flex items-center gap-3">
        <Popover open={cartOpen} onOpenChange={setCartOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="min-h-[44px]" data-testid="see-cart-button">
              <ShoppingCart className="h-4 w-4 mr-2" /> See cart
              <span className="ml-2 inline-flex items-center justify-center rounded-full text-white text-[11px] font-semibold h-5 min-w-[20px] px-1"
                style={{ background: "hsl(var(--brand))" }} data-testid="cart-count">{itemCount}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-96 p-0" data-testid="cart-panel">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Your cart</h3>
                <span className="text-[11px] text-slate-500">{itemCount} item{itemCount === 1 ? "" : "s"}</span>
              </div>
              {!cart.length ? (
                <p className="text-sm text-slate-500 py-6 text-center" data-testid="cart-empty">Your cart is empty. Add a pack to get started.</p>
              ) : (
                <>
                  <ul className="divide-y divide-slate-100 mt-1 max-h-64 overflow-auto">
                    {cart.map((l) => <CartLine key={l.key} l={l} price={linePrice(l)} onRemove={removeLine} />)}
                  </ul>
                  {estimate.length ? (
                    <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2 mt-2 text-[11px] text-slate-600" data-testid="cart-estimate">
                      <div className="font-medium text-slate-700 mb-0.5">This order gets you about</div>
                      <ul className="space-y-0.5">
                        {estimate.map((s, i) => (
                          <li key={i} className="flex items-start gap-1.5"><span className="text-emerald-600 font-semibold">≈</span><span>{s}</span></li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  <div className="border-t border-slate-200 mt-2 pt-3 space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="tabular-nums" data-testid="cart-subtotal">{fmtCAD(subtotal)}</span></div>
                    {taxQuote && taxQuote.pst_cad > 0 ? (
                      <div className="flex justify-between text-[12px] text-slate-500"><span>PST ({Math.round(taxQuote.pst_rate * 100)}%)</span><span className="tabular-nums">{fmtCAD(taxQuote.pst_cad)}</span></div>
                    ) : null}
                    {taxQuote && taxQuote.gst_hst_cad > 0 ? (
                      <div className="flex justify-between text-[12px] text-slate-500"><span>{taxQuote.gst_hst_label} ({Math.round(taxQuote.gst_hst_rate * 100)}%)</span><span className="tabular-nums">{fmtCAD(taxQuote.gst_hst_cad)}</span></div>
                    ) : null}
                    <div className="flex justify-between font-bold text-slate-900 pt-1"><span>Total</span><span className="tabular-nums" data-testid="cart-total">{fmtCAD(total)}</span></div>
                  </div>
                  <Button onClick={checkout} disabled={busy || blocked || !cart.length} className="w-full mt-3 text-white min-h-[44px]"
                    style={{ background: "hsl(var(--brand))" }} data-testid="cart-checkout">
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShoppingCart className="h-4 w-4 mr-2" />} Checkout
                  </Button>
                  <p className="text-[10px] text-slate-400 mt-2 text-center">Saved card → charged instantly. No card → secure payment page.</p>
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <p className="text-[11px] text-slate-500">{blocked ? "Activate your subscription to buy credits." : "Review your order before anything is charged."}</p>
      </div>

      <BuildYourOwnDialog open={buildOpen} onOpenChange={setBuildOpen} pricing={pricing} onAdd={addCustom} />
      <TransactionApprovedDialog open={!!success} onOpenChange={(v) => { if (!v) setSuccess(null); }} txn={success?.txn} mode={success?.mode} />
    </section>
  );
}
